import React from 'react';
import { NavLink } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../../../services/api';
import { OutletContextParams } from './delivery-orders-screen';
import { useIntl } from 'react-intl';
import { useRbac } from '../../common/Rbac';
import { RbacObject } from '../../../export-types/cleaned-types';
import { useProfile } from '../../../api/profile';
import { userIsSupplier } from '../../../utils/common.utils';
import {
  TransportationLinesCount,
  TransportationsCount,
} from './transportation-lines-count';

type DeliveryOrderProps = {
  status:
    | 'processing'
    | 'assembly'
    | 'packaged'
    | 'sentToRussia'
    | 'receivedInRussia'
    | 'receivedInRetailRussia'
    | 'sentToRecipient'
    | 'delivered'
    | 'retail';
};

export const DeliveryOrdersTabs = ({
  debouncedInternalId,
  linked,
  attention,
  isGBS,
  selectSupplier,
  selectedMethods,
  selectedLocalMethods,
}: OutletContextParams) => {
  const intl = useIntl();
  const { hasPermission } = useRbac();
  const { data: user } = useProfile();
  const canRead = hasPermission(RbacObject.DeliveryOrder, 'read');
  const canReadTransit =
    canRead || hasPermission(RbacObject.DeliveryOrder, 'read:transit');
  const tabs = [
    ...(canRead && !userIsSupplier(user)
      ? [
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.processing',
              defaultMessage: 'Заморожен',
              description: 'Надпись на вкладке',
            }),
            path: 'processing',
            name: 'processing' as const,
          },
        ]
      : []),
    ...(canRead && !userIsSupplier(user)
      ? [
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.retail',
              defaultMessage: 'Розница',
              description: 'Надпись на вкладке',
            }),
            path: 'retail',
            name: 'retail' as const,
          },
        ]
      : []),
    ...(canRead
      ? [
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.onPicking',
              defaultMessage: 'На комплектации',
              description: 'Надпись на вкладке',
            }),
            path: 'assembly',
            name: 'assembly' as const,
            countElement: (
              <TransportationLinesCount
                status={['created', 'packaged', 'packagedCombined']}
                name={debouncedInternalId}
                linked={linked}
                attention={attention}
                isGBS={isGBS}
                supplierId={selectSupplier}
                localMethodsIds={selectedLocalMethods.map(
                  (method) => method.id,
                )}
                methodsIds={selectedMethods.map((method) => method.id)}
              />
            ),
          },
        ]
      : []),
    ...(canReadTransit
      ? [
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.InTransitToAWarehouseInRussia',
              defaultMessage: 'В пути на склад в РФ',
              description: 'Надпись на вкладке',
            }),
            path: 'sent-to-russia',
            name: 'sentToRussia' as const,
            countElement: (
              <TransportationLinesCount
                status={['sent']}
                name={debouncedInternalId}
                linked={linked}
                attention={attention}
                isGBS={isGBS}
                supplierId={selectSupplier}
                localMethodsIds={selectedLocalMethods.map(
                  (method) => method.id,
                )}
                methodsIds={selectedMethods.map((method) => method.id)}
              />
            ),
          },
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.inTheWarehouseInRussia',
              defaultMessage: 'На складе в РФ',
              description: 'Надпись на вкладке',
            }),
            path: 'received-in-russia',
            name: 'receivedInRussia' as const,
          },
          {
            title: intl.formatMessage({
              id: 'TransactiosHistrory.lable.receivedInRetailRussia',
              defaultMessage: 'На складе в РФ (Розница)',
              description: 'Надпись на кнопке/вкладке',
            }),
            path: 'received-in-retail-russia',
            name: 'receivedInRetailRussia' as const,
          },
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.transferredToTheShippingCompany',
              defaultMessage: 'Переданые в ТК',
              description: 'Надпись на вкладке',
            }),
            path: 'sent-to-recipient',
            name: 'sentToRecipient' as const,
          },
        ]
      : []),
    ...(canRead
      ? [
          {
            title: intl.formatMessage({
              id: 'deliveryOrders.tab-label.received',
              defaultMessage: 'Полученные',
              description: 'Надпись на вкладке',
            }),
            path: 'delivered',
            name: 'delivered' as const,
          },
        ]
      : []),
    ...(canReadTransit
      ? [
          {
            title: intl.formatMessage({
              id: 'tabs.lable.archive',
              defaultMessage: 'Арxив',
              description: 'Надпись на вкладке/Статус заказа',
            }),
            path: 'archive',
            name: 'archive' as const,
            countElement: (
              <TransportationsCount
                status={['received']}
                types={['combined']}
                name={debouncedInternalId}
                linked={linked}
                attention={attention}
                isGBS={isGBS}
                supplierId={selectSupplier}
                localMethodsIds={selectedLocalMethods.map(
                  (method) => method.id,
                )}
                methodsIds={selectedMethods.map((method) => method.id)}
              />
            ),
          },
        ]
      : []),
  ];
  const { data, isLoading } = useSWR<{
    items: Record<DeliveryOrderProps['status'], number | undefined>;
  }>(
    {
      url: '/delivery-order/total-by-status',
      params: {
        linked,
        attention,
        isGBS,
        withoutComposite: true,
        internalId: debouncedInternalId ? debouncedInternalId : undefined,
        supplierId: selectSupplier,
        localMethodsIds: selectedLocalMethods.map((method) => method.id),
        methodsIds: selectedMethods.map((method) => method.id),
      },
    },
    fetcher,
  );

  const count = React.useMemo(() => {
    if (!data) {
      return {};
    }

    return {
      processing: data.items.processing || 0,
      retail: data.items.retail || 0,
      assembly: (data.items.assembly || 0) + (data.items.packaged || 0),
      sentToRussia: data.items.sentToRussia || 0,
      receivedInRussia: data.items.receivedInRussia || 0,
      receivedInRetailRussia: data.items.receivedInRetailRussia || 0,
      sentToRecipient: data.items.sentToRecipient || 0,
      delivered: data.items.delivered || 0,
    };
  }, [data]);

  function renderTabCount(
    item: typeof tabs extends Array<infer T> ? T : never,
  ) {
    if (item.countElement) {
      return item.countElement;
    }

    if (isLoading) {
      return (
        <>
          <i className="bi bi-three-dots color-gray-400" />
        </>
      );
    }

    if (typeof count[item.name] === 'undefined') {
      return null;
    }

    return <>{count[item.name]}</>;
  }

  return (
    <ul className="nav nav-pills d-inline-flex">
      {tabs.map((item) => (
        <li className="nav-item" key={item.title}>
          <NavLink to={item.path} className="nav-link text-decoration-none">
            {item.title} ({renderTabCount(item)})
          </NavLink>
        </li>
      ))}
    </ul>
  );
};
